import { CoreStatusExtended } from '../offer';

export interface IGenderFormat {
  male: string;
  female: string;
  other: string;
}

export interface IBoolFormat {
  true: string | boolean;
  false: string | boolean;
}

export interface IFormat {
  type: string;
  uniFormat: string | IGenderFormat | IBoolFormat;
  shieldFormat: string | IGenderFormat | IBoolFormat;
  display?: string;
}

export interface IImportFieldMapping {
  format: string;
  setup: string;
  uniField: string;
  validationType: string;
  regexStr?: string;
  momentFormat?: string;
  mappingType?: string;
}
export interface IImportFieldMappings {
  [key: string]: IImportFieldMapping;
}
export interface IFieldMappings {
  [key: string]: string;
}
export interface IMandatoryFieldMappings {
  [key: string]: string;
}
export interface IFormatFieldMappings {
  [key: string]: IFormat;
}
export interface IRegexFieldMappings {
  [key: string]: string;
}
export interface IDefaultRegexFieldMappings {
  [key: string]: string;
}
export interface IFormatMappingFields {
  [key: string]: string;
}
export interface IMultiColumnDelimeterMappings {
  [key: string]: string; // ?
}
export interface IMomentMappings {
  [key: string]: string;
}
export interface IDisabledMappings {
  [key: string]: string;
}
export interface IMappingTypes {
  [key: string]: {
    [key: string]: string | boolean;
  };
}

export interface IImportConfig {
  fieldMappings: IFieldMappings;
  mandatoryFieldMappings: IMandatoryFieldMappings;
  formatFieldMappings: IFormatFieldMappings;
  regexFieldMappings: IRegexFieldMappings;
  defaultRegexFieldMappings: IDefaultRegexFieldMappings;
  formatMappingFields: IFormatMappingFields;
  excessFields: string[];
  orphanedFields: string[];
  multiColumnDelimeterMappings: IMultiColumnDelimeterMappings;
  momentMappings: IMomentMappings;
  disabledMappings: IDisabledMappings;
  mappingTypes: IMappingTypes;
  importFieldMappings: IImportFieldMappings;
}

export enum ImportFieldTypes {
  GENERAL = 'general student',
  OFFER = 'offer',
  COURSE = 'course'
}

export interface IImportCourse {
  id?: string;
  title?: string;
  conditional?: boolean;
  isDeclined?: boolean;
  conditionText1?: string;
  conditionText2?: string;
  conditionText3?: string;
  conditionText4?: string;
  conditionText5?: string;
  conditionText6?: string;
  conditionText7?: string;
  conditionText8?: string;
  conditionText9?: string;
  cah3?: string;
  level?: string;
  secondaryLevel?: string;
  startDate?: string;
  endDate?: string;
  lastAcceptanceDate?: string;
  isFullTime?: boolean;
  hoursPerWeek?: number;
  isATASRequired?: boolean;
  studyAddressLine1?: string;
  studyAddressLine2?: string;
  studyAddressLine3?: string;
  studyCity?: string;
  studyCountry?: string;
  studyPostcode?: string;
  isAccomProvided?: boolean;
  isAccomIncludedCourseFee?: boolean;
  // 0 and undefined have different meanings for imported course fee
  // courseFeeFirstYear === undefined - no course fee data provided
  // courseFeeFirstYear === 0 - valid, course fee data provided, course fee is 0
  courseFeeFirstYear?: number;
  courseScholarshipTitle1?: string;
  courseScholarshipAmount1?: number;
  isPSEnglish?: boolean;
  isWorkPlacement?: boolean;
  workPlacementPerc?: number;
  workPlacementText?: string;
  workPlacementAddressLine1?: string;
  workPlacementAddressLine2?: string;
  workPlacementAddressLine3?: string;
  workPlacementCity?: string;
  workPlacementCountry?: string;
  workPlacementPostcode?: string;
  innerLondon?: boolean;
  offerEvidence?: string;
  coursesAccepted?: boolean;
  qual_hecos1?: string;
  qual_hecos2?: string;
  qual_hecos3?: string;
  qual_hecos4?: string;
  qual_hecos5?: string;
  qual_proportion1?: number;
  qual_proportion2?: number;
  qual_proportion3?: number;
  qual_proportion4?: number;
  qual_proportion5?: number;
  qual_cat?: string;
  faculty?: string;
  studyCounty?: string;
  workPlacementCounty?: string;
}

export interface IImportOffer {
  intakeId: string;
  offerId: string;
  applicationId?: string; // synced from apply only
  courses: Array<IImportCourse>;
  isSELTReqd?: boolean;
  reasonSELTNotReqd?: string;
  previousCourseLevel?: string;
  courseProgression?: string;
  progressionText?: string;
  docsUsedToObtainOffer?: string;
  courseFeePaidToDate?: number;
  accomFeePaidToDate?: number;
  uniStatus?: CoreStatusExtended;
  uniCancelled?: boolean;
  externalCasStatus?: TExternalCasStatus;
  casBypass?: boolean;
  withAgent?: boolean;
  agentName?: string;
  agentCompany?: string;
  agentOffice?: string;
  agentCompanyEmail?: string;
  agentEmail?: string;
  scholarshipTitle?: string;
  scholarshipAmount?: number;
  calendlyInterviewTimestamp?: string;
  sponsorProvideAccom?: boolean;
  fy1CourseFeeIncludeAccom?: boolean;
  accomFee?: number;
  fy1BoardingFees?: number;
  apfSeqn?: string;
  capSeqn?: string;
  courseCode?: string;
  ayrCode?: string;
  monthCode?: string;
  isPISectionStarted?: boolean;
  isPISectionComplete?: boolean;
  isHSectionStarted?: boolean;
  isHSectionComplete?: boolean;
}

export interface IImportData {
  source?: ImportSource;
  studentNo: string;
  applicantId?: string;
  uniPartnerId?: string;
  email: string;
  uni?: string;
  pathway?: string;
  familyName: string;
  givenName: string;
  otherName?: string;
  dob?: string;
  phone?: string;
  nationality?: string;
  placeOfBirth?: string;
  countryOfBirth?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  country?: string;
  postcode?: string;
  passportNumber?: string;
  passportExpiry?: string;
  passportSeqn?: string;
  ucasId?: string;
  applicationId?: string; // used for cas shield only. it's not the same as the one in apply (that one is at offer level)
  englishLevelAttained?: string;
  seltSpeaking?: string;
  seltListening?: string;
  seltReading?: string;
  seltWriting?: string;
  englishTestProvider?: string;
  prevUKStudy?: boolean;
  criminalConvictions?: boolean;
  previousCourseCompletion?: string;
  applicationDate?: string;
  confirmed?: boolean;
  offers: Array<IImportOffer>;
  withAgent?: boolean;
  agentName?: string;
  agentCompany?: string;
  agentOffice?: string;
  agentCompanyEmail?: string;
  agentEmail?: string;
  lastSkippedImportTime?: number;
  uniStatus?: string;
  uniCancelled?: boolean;
  sponsorName?: string;
  overseasHigherEducationInstitutionDetails?: string;
  passportVisSeqn?: string;
  userDefined1?: string;
  userDefined2?: string;
  userDefined3?: string;
  userDefined4?: string;
  userDefined5?: string;
  userDefined6?: string;
  userDefined7?: string;
  userDefined8?: string;
  userDefined9?: string;
  userDefined10?: string;
  userDefined11?: string;
  userDefined12?: string;
  userDefined13?: string;
  userDefined14?: string;
  userDefined15?: string;
  userDefined16?: string;
  userDefined17?: string;
  userDefined18?: string;
  userDefined19?: string;
  userDefined20?: string;
  reasonSELTNotReqd?: string;
  qualificationEndDate?: string;
  courseFeePaidToDate?: number;
  accomFeePaidToDate?: number;
  importHash?: string;
  admissionsOffice?: string;
}

export interface IImportStudentField {
  summaryField: string | null;
  summaryFieldType?: string;
  name: string | null;
  type: ImportFieldTypes;
  isPresessional: boolean | null;
  label: string;
}

export interface IImportStudentFields {
  [key: string]: IImportStudentField;
}

export enum TExternalCasStatus {
  ASSIGNED = 'ASSIGNED',
  WITHDRAWN = 'WITHDRAWN',
  EXPIRED = 'EXPIRED',
  USED = 'USED',
  CANCELLED = 'CANCELLED'
}

export interface IImportDoc {
  p_key: string;
  s_key: string;
  docType: string;
  data: IImportData;
  lastUpdatedAction: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  lastUpdatedRole: string;
  lastUpdatedTime: string;
}

export enum ImportSource {
  CSV = 'CSV',
  SFTP = 'SFTP',
  SITS = 'SITS',
  APPLY = 'APPLY'
}
